import { EquipmentImport } from "../../components/physical/EquipmentImport"
import { App } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { Alert, Button, Empty, Pagination, Space, Spin, Switch, Tag, Typography } from "antd"
import { useState } from "react"
import { Link } from "react-router-dom"
import type { PhysicalIdentity, PhysicalKind } from "../../types/physical"
import { physicalApi, physicalError } from "../../utils/physicalApi"
import { usePhysicalResource } from "../../components/physical/usePhysicalResource"
import { IdentityEditor } from "../../components/physical/IdentityEditor"
export function PhysicalInventory({
  kind,
  parent,
}: {
  kind: "asset" | "component"
  parent: PhysicalIdentity
}) {
  const { message } = App.useApp()
  const [importing, setImporting] = useState(false)
  const [page, setPage] = useState(1)
  const [archived, setArchived] = useState(false)
  const [create, setCreate] = useState(false)
  const resource = usePhysicalResource(
    `inventory/${kind}/${parent.uuid}/${page}/${archived}`,
    (signal) =>
      physicalApi.list(
        kind,
        {
          limit: 24,
          offset: (page - 1) * 24,
          archived,
          ...(kind === "asset"
            ? { physicalSiteUuid: parent.uuid }
            : { physicalAssetUuid: parent.uuid }),
        },
        signal,
      ),
  )
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div className="physical-heading">
        <label>
          <Switch
            checked={archived}
            onChange={(v) => {
              setArchived(v)
              setPage(1)
            }}
          />{" "}
          Afficher les éléments archivés
        </label>
        {kind === "asset" && (
          <Button disabled={parent.archived} onClick={() => setImporting(true)}>
            Importer depuis les inspections
          </Button>
        )}
        <Button icon={<PlusOutlined />} disabled={parent.archived} onClick={() => setCreate(true)}>
          {kind === "asset" ? "Ajouter un équipement physique" : "Ajouter un composant physique"}
        </Button>
      </div>
      {parent.archived && (
        <Alert
          type="info"
          message="Ce dossier est archivé. Réactivez-le avant d’ajouter de nouveaux éléments."
        />
      )}
      {resource.error && (
        <Alert
          type="error"
          message={physicalError(resource.error)}
          action={<Button onClick={resource.reload}>Réessayer</Button>}
        />
      )}
      {resource.loading && <Spin />}
      {resource.data && (
        <>
          {resource.data.items.length === 0 ? (
            <Empty description="Aucun élément dans cet inventaire" />
          ) : (
            <div className="physical-grid">
              {resource.data.items.map((item) => (
                <Link
                  className="physical-tile"
                  to={`/${kind === "asset" ? "physical-assets" : "physical-components"}/${item.uuid}`}
                  key={item.uuid}
                >
                  <Space>
                    {item.code && <Tag>{item.code}</Tag>}
                    {item.archived && <Tag>Archivé</Tag>}
                  </Space>
                  <h3>{item.name}</h3>
                  <Typography.Text type="secondary">
                    {[item.manufacturer, item.model, item.serialNumber]
                      .filter(Boolean)
                      .join(" · ") ||
                      (kind === "component"
                        ? "Composant permanent de cet équipement"
                        : "Équipement permanent du site")}
                  </Typography.Text>
                  <span>Consulter les inspections →</span>
                </Link>
              ))}
            </div>
          )}
          <Pagination
            current={page}
            pageSize={24}
            total={resource.data.total}
            onChange={setPage}
            showSizeChanger={false}
          />
        </>
      )}
      {importing && (
        <EquipmentImport
          site={parent}
          onCancel={() => setImporting(false)}
          onImported={(result) => {
            setImporting(false)
            setPage(1)
            setArchived(false)
            resource.reload()
            message.success(
              `${result.assetsCreated} équipement(s) et ${result.componentsCreated} composant(s) créés ; ${result.equipmentLinked} équipement(s) et ${result.componentsLinked} composant(s) rattachés.`,
            )
          }}
        />
      )}
      {create && (
        <IdentityEditor
          kind={kind as PhysicalKind}
          parent={parent.uuid}
          onCancel={() => setCreate(false)}
          onSaved={() => {
            setCreate(false)
            setPage(1)
            resource.reload()
          }}
        />
      )}
    </Space>
  )
}
